/**
 * Projekt suchen beim Zuordnen: Name, Kunde oder Adresse — alle Projekte, gleich
 * welcher Status. Ohne Eingabe stehen die nächstgelegenen oben.
 */
import { useId, useMemo, useRef, useState } from "react";
import type { ZuordnenProjekt } from "../../hooks/useMedien";
import { statusFarbe } from "./FotoKarte";

export function meter(b1: number, l1: number, b2: number, l2: number): number {
  const r = 6_371_000;
  const rad = Math.PI / 180;
  const db = (b2 - b1) * rad;
  const dl = (l2 - l1) * rad;
  const a = Math.sin(db / 2) ** 2 + Math.cos(b1 * rad) * Math.cos(b2 * rad) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

export function entfernung(m: number | null): string {
  if (m == null) return "";
  if (m < 1000) return `${Math.round(m / 10) * 10} m`;
  return `${(m / 1000).toLocaleString("de-DE", { maximumFractionDigits: m < 10_000 ? 1 : 0 })} km`;
}

const glatt = (t: string) => t.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ß/g, "ss");
const ohneZeichen = (status: string) => status.replace(/^[^\p{L}]+/u, "");

type Props = {
  projekte: ZuordnenProjekt[];
  /** Mitte des Bündels — dafür die Entfernungen. */
  mitte: { breite: number; laenge: number } | null;
  gewaehlt: ZuordnenProjekt | null;
  onWahl: (p: ZuordnenProjekt | null) => void;
};

type Treffer = { p: ZuordnenProjekt; m: number | null };

export default function ProjektSuche({ projekte, mitte, gewaehlt, onWahl }: Props) {
  const [text, setText] = useState("");
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState(0);
  const listeId = useId();
  const feld = useRef<HTMLInputElement>(null);

  const mitAbstand = useMemo<Treffer[]>(() => projekte.map((p) => ({
    p,
    m: mitte && p.breite != null && p.laenge != null ? meter(mitte.breite, mitte.laenge, p.breite, p.laenge) : null,
  })), [projekte, mitte]);

  const treffer = useMemo<Treffer[]>(() => {
    const q = glatt(text.trim());
    const nachAbstand = (a: Treffer, b: Treffer) => (a.m ?? Infinity) - (b.m ?? Infinity);
    if (!q) return mitAbstand.filter((t) => t.m != null && t.m <= 5000).sort(nachAbstand).slice(0, 8);
    const woerter = q.split(/\s+/);
    return mitAbstand
      .map((t) => {
        const heu = glatt(`${t.p.titel} ${t.p.kunde} ${t.p.adresse}`);
        if (!woerter.every((w) => heu.includes(w))) return null;
        const vorn = glatt(t.p.titel).startsWith(woerter[0]) || glatt(t.p.kunde).startsWith(woerter[0]);
        return { ...t, rang: vorn ? 0 : 1 };
      })
      .filter((t): t is Treffer & { rang: number } => !!t)
      .sort((a, b) => a.rang - b.rang || nachAbstand(a, b))
      .slice(0, 30);
  }, [text, mitAbstand]);

  function waehlen(t: Treffer) {
    onWahl(t.p);
    setText("");
    setOffen(false);
  }

  const gewaehltAbstand = gewaehlt ? mitAbstand.find((t) => t.p.projekt_id === gewaehlt.projekt_id)?.m ?? null : null;

  return (
    <div className="fo-suche">
      {gewaehlt && !offen ? (
        <button type="button" className="fo-suche-gewaehlt" onClick={() => { setOffen(true); setTimeout(() => feld.current?.focus(), 0); }}>
          <i style={{ background: statusFarbe(gewaehlt.status) }} aria-hidden="true" />
          <span>
            <strong>{gewaehlt.titel || "Projekt"}</strong>
            <small>{[gewaehlt.kunde, ohneZeichen(gewaehlt.status), entfernung(gewaehltAbstand)].filter(Boolean).join(" · ")}</small>
          </span>
          <em>ändern</em>
        </button>
      ) : (
        <input
          ref={feld}
          type="search"
          role="combobox"
          aria-expanded={offen}
          aria-controls={listeId}
          aria-autocomplete="list"
          placeholder="Projekt suchen — Name, Kunde oder Straße"
          value={text}
          onChange={(e) => { setText(e.target.value); setOffen(true); setMarkiert(0); }}
          onFocus={() => setOffen(true)}
          onBlur={() => setTimeout(() => setOffen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setOffen(true); setMarkiert((i) => Math.min(i + 1, treffer.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setMarkiert((i) => Math.max(i - 1, 0)); }
            else if (e.key === "Enter" && treffer[markiert]) { e.preventDefault(); waehlen(treffer[markiert]); }
            else if (e.key === "Escape") { setOffen(false); }
          }}
        />
      )}
      {offen && (
        <ul className="fo-suche-liste" id={listeId} role="listbox">
          {!text.trim() && treffer.length > 0 && <li className="fo-suche-kopf" aria-hidden="true">In der Nähe</li>}
          {treffer.map((t, i) => (
            <li
              key={t.p.projekt_id}
              role="option"
              aria-selected={i === markiert}
              className={i === markiert ? "is-markiert" : ""}
              onMouseDown={(e) => { e.preventDefault(); waehlen(t); }}
              onMouseEnter={() => setMarkiert(i)}
            >
              <i style={{ background: statusFarbe(t.p.status) }} aria-hidden="true" />
              <span>
                <strong>{t.p.titel || "Projekt"}</strong>
                <small>{[t.p.kunde, ohneZeichen(t.p.status), t.p.breite == null ? "nicht auf der Karte" : ""].filter(Boolean).join(" · ")}</small>
              </span>
              {t.m != null && <em>{entfernung(t.m)}</em>}
            </li>
          ))}
          {treffer.length === 0 && (
            <li className="fo-suche-leer" aria-hidden="true">
              {text.trim() ? "Kein Projekt gefunden." : "Kein Projekt in der Nähe — Namen eintippen."}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
